import React, {useEffect, useState} from 'react';
import {optimizedPhotographicScene} from '../../utils/configuratorImages';
import './PhotographicLayers.css';

function useAsset(src) {
  const [loaded, setLoaded] = useState({src:null, status:'loading'});
  useEffect(() => {
    if (!src) return undefined;
    let live = true;
    const image = new Image();
    image.onload = () => live && setLoaded({src, status:'ready'});
    image.onerror = () => live && setLoaded({src, status:'error'});
    image.src = src;
    return () => {live = false; image.onload = null; image.onerror = null;};
  }, [src]);
  return !src ? 'ready' : loaded.src === src ? loaded.status : 'loading';
}

function usePanelAssets(panels, body, mask) {
  // Unique sources only: three equal colours load one atlas, not three copies.
  const key=JSON.stringify([...new Set([mask,...(panels||[]).map(panel=>panel.src)])]
    .filter(src=>src&&src!==body));
  const [loaded,setLoaded]=useState({key:null,status:'loading'});
  useEffect(()=>{
    const sources=JSON.parse(key);
    if(!sources.length)return undefined;
    let live=true,pending=sources.length,failed=false;
    const images=sources.map(src=>{
      const image=new Image();
      image.onload=()=>{
        pending-=1;
        if(live&&!failed&&pending===0)setLoaded({key,status:'ready'});
      };
      image.onerror=()=>{failed=true;if(live)setLoaded({key,status:'error'});};
      image.src=src;
      return image;
    });
    return ()=>{live=false;images.forEach(image=>{image.onload=null;image.onerror=null;});};
  },[key]);
  return key==='[]'?'ready':loaded.key===key?loaded.status:'loading';
}

// Same body DOM node and source on ribbon changes. All geometry is proportional
// to the contained square scene, including narrow thumbnails and landscape zoom.
export default function PhotographicLayers({scene: originalScene, label, decorative = false, className = ''}) {
  const scene = optimizedPhotographicScene(originalScene);
  const bodyStatus = useAsset(scene.body);
  const ribbonStatus = useAsset(scene.ribbon);
  const maskStatus = useAsset(scene.junctionMask);
  const overlayStatus = useAsset(scene.junctionOverlay);
  const panelStatus = usePanelAssets(scene.bodyPanels,scene.body,scene.bodyMask);
  const statuses = [bodyStatus, ribbonStatus, maskStatus, overlayStatus,panelStatus];
  const status = statuses.includes('error') ? 'error' : statuses.includes('loading') ? 'loading' : 'ready';
  const b = scene.bodyPlacement, r = scene.ribbonPlacement;
  const bodyStyle = {left:`${b.x*100}%`, top:`${b.y*100}%`, width:`${b.width*100}%`};
  return <div className={`bookmark-art photographic-layers ${className}`}
    role={decorative ? undefined : 'img'} aria-label={decorative ? undefined : label}
    aria-hidden={decorative || undefined} aria-busy={status === 'loading'}
    data-render-mode="photo-layers" data-state={status}>
    <div className="pl-frame"><div className="pl-scene" style={{visibility:status === 'ready' ? 'visible' : 'hidden'}}>
      <div className="pl-ribbon-window" style={{maskImage:scene.junctionMask ? `url("${scene.junctionMask}")` : 'none'}}>
        <div className="pl-ribbon-pivot" style={{
          left:`${r.anchor[0]*100}%`, top:`${r.anchor[1]*100}%`, width:`${r.width*100}%`,
          transform:`rotate(${r.rotation || 0}deg)${r.mirrored ? ' scaleX(-1)' : ''}`,
        }}>
          <img data-layer="ribbon" src={scene.ribbon} alt="" draggable="false"
            style={{transform:`translate(${-r.origin[0]*100}%,${-r.origin[1]*100}%)`}} />
        </div>
      </div>
      {scene.bodyPanels ? <div className="pl-body-panels" data-layer="body"
        style={{...bodyStyle,maskImage:`url("${scene.bodyMask||scene.body}")`}}>
        {scene.bodyPanels.map(panel=><img key={panel.id} data-panel={panel.id}
          src={panel.src} alt="" draggable="false" style={{clipPath:panel.clipPath}} />)}
      </div> : <img className="pl-body" data-layer="body" src={scene.body} alt="" draggable="false"
        style={bodyStyle} />}
      {scene.junctionOverlay && <img className="pl-junction" src={scene.junctionOverlay} alt="" draggable="false"
        style={bodyStyle} />}
    </div></div>
    {status === 'loading' && <span className="pl-loading" aria-hidden="true" />}
    {status === 'error' && !decorative && <p className="pl-error" role="status">L’aperçu ne peut pas être chargé. Vos choix sont conservés.</p>}
  </div>;
}
